'use client';
import SecBox from '@/components/layout/sec-box';
import ActiveUsersData from '@/components/pages/dashboard/active-users-data';
import AlertsData from '@/components/pages/dashboard/alert-data';
import DailyNetWinChart from '@/components/pages/dashboard/daily-netwin-chart';
import MonthlyNetWinChart from '@/components/pages/dashboard/monthly-netwin-chart';
import NetIncome from '@/components/pages/dashboard/net-income';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { Flex, Grid } from '@mantine/core';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { generateAuth } from '@/lib/utils/axios';
import { setIsClear } from '@/lib/features/notification/notificationSlice';

export default function Dashboard() {
    const { languageData } = useSelector((state) => state.language);

    const dashboardTxt = languageData?.text?.dashboard;
    const commonTxt = languageData?.text?.common;
    const { xxlDevice, mdDevice, smDevice } = useBreakpoints();
    const { remoteId } = useSelector((state) => state.session);
    const [notificationData, setNotificationData] = useState([]);
    const [error, setError] = useState({ message: '', variant: '' });
    const [userError, setUserError] = useState({ message: '', variant: '' });
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [userData, setUserData] = useState([]);
    const { alertData, isClear } = useSelector((state) => state.notification);
    const dispatch = useDispatch();

    const fetchNotificationData = useCallback(async () => {
        if (!remoteId) return;
        setLoading(true);
        setError({ message: '', variant: '' });
        const params = {
            casino: process.env.NEXT_PUBLIC_CASINO,
            AUTH_ID: remoteId,
            isRead: false,
            page: 1,
            limit: 5000,
        };
        try {
            const response = await axios.get(`${process.env.NEXT_PUBLIC_API_DOMAIN}/api/notifications/get`, {
                params,
            });
            const res = response?.data;
            if (res?.status === 200) {
                if (res?.data?.length > 0) {
                    setNotificationData(res?.data);
                    setError({ message: '', variant: '' });
                } else {
                    setNotificationData([]);
                    setError({ message: commonTxt?.no_data_msg, variant: 'info' });
                }
            } else {
                setError({ message: res?.message, variant: 'danger' });
            }
        } catch (error) {
            setError({ message: error.message, variant: 'danger' });
        } finally {
            setLoading(false);
        }
    }, [remoteId, commonTxt]);

    useEffect(() => {
        if (commonTxt && Object.keys(commonTxt).length > 0) {
            fetchNotificationData();
        }
    }, [fetchNotificationData, alertData, isClear]);

    const clearAllNotification = async () => {
        dispatch(setIsClear(true));
        const params = {
            casino: process.env.NEXT_PUBLIC_CASINO,
            AUTH_ID: remoteId,
            ids: notificationData?.map((v) => v?.id)?.join(','),
        };
        try {
            const response = await axios.patch(`${process.env.NEXT_PUBLIC_API_DOMAIN}/api/notifications/markAsRead`, null, {
                params,
            });
            const res = response?.data;
            if (res?.status === 200) {
                fetchNotificationData();
            } else {
                console.error('Failed to clear notifications:', res?.message);
            }
        } catch (error) {
            console.error('Error clearing notifications:', error?.message);
        } finally {
            dispatch(setIsClear(false));
        }
    };

    const fetchUserData = useCallback(async () => {
        if (!remoteId) return;
        setUserLoading(true);
        setUserError({ message: '', variant: '' });
        const authKey = generateAuth(`remoteId=${remoteId}`);
        const params = {
            casino: process.env.NEXT_PUBLIC_CASINO,
            remoteId: remoteId,
            limit: 1000,
            page: 1,
            authKey,
            AUTH_ID: remoteId,
            accountType: 'unlock',
        };
        try {
            const response = await axios.get(`${process.env.NEXT_PUBLIC_API_DOMAIN}/players/dashboard-users-by-remoteid`, {
                params,
            });
            const res = response?.data;
            if (res?.status === 200) {
                if (res?.data?.length > 0) {
                    setUserData(res?.data);
                    setUserError({ message: '', variant: '' });
                } else {
                    setUserData([]);
                    setUserError({ message: commonTxt?.no_data_msg, variant: 'info' });
                }
            } else {
                setUserError({ message: res?.message, variant: 'danger' });
            }
        } catch (error) {
            setUserError({ message: error.message, variant: 'danger' });
        } finally {
            setUserLoading(false);
        }
    }, [remoteId, commonTxt]);

    useEffect(() => {
        if (commonTxt && Object.keys(commonTxt).length > 0) {
            fetchUserData();
        }
    }, [fetchUserData]);

    // Height helpers — slightly reduced on tablet/mobile (below md = 768px)
    const chartHeight = xxlDevice ? '20.625rem' : mdDevice ? '20rem' : smDevice ? '16rem' : '14rem';
    const alertHeight = xxlDevice ? '20.625rem' : mdDevice ? '22rem' : smDevice ? '18rem' : '16rem';
    const bottomRowHeight = xxlDevice ? '27.063rem' : mdDevice ? '22rem' : smDevice ? '18rem' : '16rem';

    return (
        <>
            <main className={'dashboard'}>
                <Grid>
                    <Grid.Col span={12}>
                        <h1 className="page_title">{dashboardTxt?.page_title}</h1>
                    </Grid.Col>
                </Grid>
                <Grid gutter={24}>
                    <Grid.Col span={{ md: 6, xxl: 3, vvl: 3 }}>
                        <SecBox title={false} height={chartHeight}>
                            <NetIncome dashboardTxt={dashboardTxt} />
                        </SecBox>
                    </Grid.Col>
                    <Grid.Col span={{ md: 6, xxl: 4, vvl: 3 }}>
                        <SecBox title={dashboardTxt?.monthly_netwin} height={chartHeight}>
                            <MonthlyNetWinChart />
                        </SecBox>
                    </Grid.Col>
                    <Grid.Col span={{ xxl: 5, vvl: 6 }}>
                        <SecBox
                            title={dashboardTxt?.alerts}
                            height={alertHeight}
                            rightSec={
                                <Flex justify="flex-end" align="center" gap="md">
                                    <Link href={`/notification-alert`} onClick={() => clearAllNotification()} title="See all Notifications" className="see_all_btn">
                                        {commonTxt?.see_all}
                                    </Link>
                                    <button type="button" className="clear_all_btn" onClick={clearAllNotification} disabled={notificationData?.length === 0}>
                                        {commonTxt?.clear_all}
                                    </button>
                                </Flex>
                            }
                        >
                            <AlertsData notificationData={notificationData} loading={loading} error={error} />
                        </SecBox>
                    </Grid.Col>
                    <Grid.Col span={{ xxl: 6 }}>
                        <SecBox title={dashboardTxt?.daily_netwin} height={bottomRowHeight}>
                            <DailyNetWinChart />
                        </SecBox>
                    </Grid.Col>
                    <Grid.Col span={{ xxl: 6 }}>
                        <SecBox
                            title={dashboardTxt?.active_users}
                            height={bottomRowHeight}
                            rightSec={
                                <Link href={`/user-management?active=true`} title="See all Active Users" className="see_all_btn">
                                    {commonTxt?.see_all}
                                </Link>
                            }
                        >
                            <ActiveUsersData dashboardTxt={dashboardTxt} loading={userLoading} error={userError} userData={userData} />
                        </SecBox>
                    </Grid.Col>
                </Grid>
            </main>
        </>
    );
}
